import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    check_in: '',
    check_out: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
          check_in: '',
          check_out: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="min-h-screen bg-[#FAF8F5] pt-24">
      {/* Header */}
      <section className="py-16 px-6 md:px-12 lg:px-24 text-center">
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          data-testid="contact-page-title"
          className="font-serif font-bold tracking-tighter leading-none text-[#1E3A8A] mb-6"
          style={{ fontSize: '26px' }}
        >
          Get in Touch
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          data-testid="contact-page-subtitle"
          className="text-base leading-relaxed text-[#3A3A3A] max-w-3xl mx-auto"
        >
          We're here to answer any questions about your stay at De Azraa.
        </motion.p>
      </section>

      <section className="py-16 px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -60 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight font-light text-[#1A1A1A] mb-8">
              Contact Information
            </h2>

            <div className="space-y-8">
              <div className="flex items-start gap-4" data-testid="contact-address">
                <MapPin className="text-[#C9A84C] flex-shrink-0 mt-1" size={24} strokeWidth={1.5} />
                <div>
                  <h3 className="text-xs tracking-[0.2em] uppercase font-semibold text-[#1A1A1A] mb-2">Address</h3>
                  <p className="text-base leading-relaxed text-[#3A3A3A]">
                    Plot 10/9 Udoka Housing Estate Phase 1<br />
                    Awka, Anambra State, Nigeria
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-testid="contact-phone">
                <Phone className="text-[#C9A84C] flex-shrink-0 mt-1" size={24} strokeWidth={1.5} />
                <div>
                  <h3 className="text-xs tracking-[0.2em] uppercase font-semibold text-[#1A1A1A] mb-2">Phone</h3>
                  <a
                    href="tel:+2348066722221"
                    className="text-base text-[#3A3A3A] hover:text-[#C9A84C] transition-colors"
                  >
                    +234 806 672 2221
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4" data-testid="contact-email">
                <Mail className="text-[#C9A84C] flex-shrink-0 mt-1" size={24} strokeWidth={1.5} />
                <div>
                  <h3 className="text-xs tracking-[0.2em] uppercase font-semibold text-[#1A1A1A] mb-2">Email</h3>
                  <a
                    href="mailto:Azraalux@gmail.com"
                    className="text-base text-[#3A3A3A] hover:text-[#C9A84C] transition-colors"
                  >
                    Azraalux@gmail.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4" data-testid="contact-hours">
                <Clock className="text-[#C9A84C] flex-shrink-0 mt-1" size={24} strokeWidth={1.5} />
                <div>
                  <h3 className="text-xs tracking-[0.2em] uppercase font-semibold text-[#1A1A1A] mb-2">Hours</h3>
                  <p className="text-base text-[#3A3A3A]">Open 24 Hours</p>
                </div>
              </div>
            </div>

            <div className="mt-12">
              <a
                href="https://wa.me/2348066722221"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-btn"
                className="inline-block bg-[#25D366] text-white hover:bg-[#1DA851] transition-colors duration-300 px-8 py-4 text-sm tracking-widest uppercase"
              >
                Chat on WhatsApp
              </a>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 60 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <form onSubmit={handleSubmit} data-testid="contact-form" className="bg-white border border-[#E5E5E5] p-8">
              <h2 className="font-serif text-2xl sm:text-3xl tracking-tight font-light text-[#1A1A1A] mb-8">
                Send Us a Message
              </h2>

              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-xs tracking-[0.2em] uppercase font-semibold text-[#1A1A1A] mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    data-testid="contact-name-input"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full border border-[#E5E5E5] px-4 py-3 text-[#1A1A1A] focus:outline-none focus:border-[#C9A84C] transition-colors"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-xs tracking-[0.2em] uppercase font-semibold text-[#1A1A1A] mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    data-testid="contact-email-input"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full border border-[#E5E5E5] px-4 py-3 text-[#1A1A1A] focus:outline-none focus:border-[#C9A84C] transition-colors"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-xs tracking-[0.2em] uppercase font-semibold text-[#1A1A1A] mb-2">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    data-testid="contact-phone-input"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full border border-[#E5E5E5] px-4 py-3 text-[#1A1A1A] focus:outline-none focus:border-[#C9A84C] transition-colors"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="check_in" className="block text-xs tracking-[0.2em] uppercase font-semibold text-[#1A1A1A] mb-2">
                      Check-in (Optional)
                    </label>
                    <input
                      type="date"
                      id="check_in"
                      name="check_in"
                      data-testid="contact-checkin-input"
                      value={formData.check_in}
                      onChange={handleChange}
                      className="w-full border border-[#E5E5E5] px-4 py-3 text-[#1A1A1A] focus:outline-none focus:border-[#C9A84C] transition-colors"
                    />
                  </div>

                  <div>
                    <label htmlFor="check_out" className="block text-xs tracking-[0.2em] uppercase font-semibold text-[#1A1A1A] mb-2">
                      Check-out (Optional)
                    </label>
                    <input
                      type="date"
                      id="check_out"
                      name="check_out"
                      data-testid="contact-checkout-input"
                      value={formData.check_out}
                      onChange={handleChange}
                      className="w-full border border-[#E5E5E5] px-4 py-3 text-[#1A1A1A] focus:outline-none focus:border-[#C9A84C] transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-xs tracking-[0.2em] uppercase font-semibold text-[#1A1A1A] mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    data-testid="contact-message-input"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    className="w-full border border-[#E5E5E5] px-4 py-3 text-[#1A1A1A] focus:outline-none focus:border-[#C9A84C] transition-colors resize-none"
                  />
                </div>

                {submitStatus === 'success' && (
                  <div data-testid="contact-success-message" className="p-4 bg-green-50 border border-green-200 text-green-800">
                    Thank you! We'll get back to you soon.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div data-testid="contact-error-message" className="p-4 bg-red-50 border border-red-200 text-red-800">
                    Something went wrong. Please try again or call us directly.
                  </div>
                )}

                <button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={isSubmitting}
                  className="w-full bg-[#1A1A1A] text-[#FAF8F5] hover:bg-[#C9A84C] hover:text-[#1A1A1A] transition-colors duration-300 px-8 py-4 text-sm tracking-widest uppercase disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  <Send size={16} />
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 px-6 md:px-12 lg:px-24" data-testid="contact-map-section">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="h-[500px]"
        >
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3964.2!2d7.0719!3d6.2103!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNsKwMTInMzcuMSJOIDfCsDA0JzE4LjgiRQ!5e0!3m2!1sen!2sng!4v1234567890"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            title="De Azraa Location Map"
          />
        </motion.div>
      </section>
    </div>
  );
};

export default Contact;